import { OperationOptions, SelectOption , Operations, SingleDigitNumbers, RegroupingOptions, FractionDenominatorOptions, MissingNumberOptions, ProblemFormatOptions, PlaceValueOptions, RoundingComparingOptions, RoundingPlaceOptions, RoundingDisplayOptions, TimeQuestionOptions, TimeIntervalOptions, TimeDisplayOptions, MoneyQuestionOptions, DEFAULT_MONEY_COINS, OrderOfOperationsLevelOptions, FactFamilyOptions, FractionQuestionOptions, StoryTypeOptions, AlgebraQuestionOptions, IntegerQuestionOptions, RationalKindOptions, NumberPropertyOptions, ConversionQuestionOptions, PatternQuestionOptions, NumberLineQuestionOptions, PercentQuestionOptions, RatioQuestionOptions, ShapeQuestionOptions, AverageQuestionOptions, BarGraphQuestionOptions, GeometryQuestionOptions, RomanQuestionOptions, VolumeQuestionOptions, CircleQuestionOptions, ProbabilityQuestionOptions, PythagoreanQuestionOptions, LinePlotQuestionOptions, LinePlotScaleOptions, LinePlotMarkOptions, CoordinateQuestionOptions, CoordinateGridOptions, CalendarQuestionOptions, CalendarSpanOptions, CalendarMonthOptions, HistogramQuestionOptions, BoxPlotQuestionOptions} from "./Constants.ts";
import { PrintedLanguage } from "./PrintedLanguage.ts";

export interface NumberRange{
    Id: number;
    NumberOfDigits: number;
    NumberRange: number[];
    NumberRangeSelection: number[];
}

export interface ISettingsProps {
    /**
     * What kind of problem the sheet is built from. These are mutually
     * exclusive - a sheet is math facts or word problems or fractions or
     * money - which is why it is one field rather than a boolean each. The one
     * exception is `SpiralReview`, whose job is to compose the others into
     * labelled sections (ADR 0013); its section list is `SpiralSections`.
     */
    Format: ProblemFormatOptions,
    AllOperations: boolean,
    Page: Operations
    Operators: SelectOption[];
    NumberRanges: NumberRange[];
    HalfPage: boolean;
    NumberOfProblemsPerPage: number;
    NumberOfPages: number;    
    AnswerPages: boolean;
    /**
     * Show the working on the answer key, not just the answer - carried digits,
     * partial products, the long-division algorithm, the common-denominator
     * steps. Meaningful only when AnswerPages is on. See ADR 0015.
     */
    WorkedAnswers: boolean;
    /**
     * How the problems are drawn, independent of what they are (ADR 0014):
     * ruled carry boxes, faint alignment guides, large print. A grouped field
     * so a future accommodation is added here rather than scattered.
     */
    Presentation: {
        carryBoxes: boolean;
        alignmentGrid: boolean;
        largePrint: boolean;
    };
    /**
     * The sections of a spiral-review sheet: a format and how many problems of
     * it. Non-empty only when Format is SpiralReview. See ADR 0013.
     */
    SpiralSections: { format: ProblemFormatOptions; count: number }[];
    Remainders: boolean;
    Regrouping: RegroupingOptions;
    /** Which cell of a math fact is left blank. Math Facts format only. */
    MissingNumber: MissingNumberOptions;
    /**
     * Which stories a word-problem sheet draws (ADR 0073 section 2). Word Problems format
     * only. Simple is the default, and prints exactly what every sheet printed before
     * Varied existed, so a printed code still rebuilds its sheet.
     */
    StoryTypes: StoryTypeOptions;
    /** Prints a Time and Score box on the worksheet, for timed fact drills. */
    Timed: boolean;
    /**
     * The fact fluency level the sheet tests, or 0 for none (ADR 0071 §5). When
     * it applies - a math-facts sheet whose operations have a ladder, see
     * `levelApplies` in FactLevels.ts - the facts are dealt from the level and
     * `NumberRanges` is ignored but kept, so turning levels off gives the
     * visitor's ranges back.
     */
    FactLevel: number;
    /** How far the multiplication and division tables run: to 10 or to 12 (ADR 0071 §2). */
    FactLevelCeiling: 10 | 12;
    /**
     * Whether a level sheet is the test or the home practice for it (ADR 0071 §8): the
     * level's own facts only, a signature line in place of the clock, the key off. Only
     * read where `levelApplies`.
     */
    FactLevelSheet: 'test' | 'practice';
    /** Whether fraction pairs share a denominator. Addition and subtraction only. */
    FractionDenominators: FractionDenominatorOptions;
    /** Largest denominator a generated fraction may use. */
    MaxDenominator: number;
    /** Largest money amount a problem may use, in dollars. */
    MoneyMaxDollars: number;
    /** What a money sheet asks: arithmetic (default), counting coins, making change. */
    MoneyQuestion: MoneyQuestionOptions;
    /** Which coins a counting-coins problem may show, by name. */
    MoneyCoins: string[];
    /** Most decimal places a decimal problem may use (1-3). */
    DecimalPlaces: number;
    /** Largest whole-number part a decimal problem may use. */
    DecimalMaxWhole: number;
    /** Which way round a place value question is asked. */
    PlaceValueQuestion: PlaceValueOptions;
    /** How many digits a place value number has. */
    PlaceValueDigits: number;
    /** Whether a sheet rounds numbers, compares them, or both. */
    RoundingComparingQuestion: RoundingComparingOptions;
    /** The place numbers are rounded to. Ignored when the question is Comparing. */
    RoundingPlace: RoundingPlaceOptions;
    /** Digits in a whole number to round or compare (2-5). */
    RoundingComparingDigits: number;
    /** Decimal places for a rounding/comparing sheet; 0 means whole numbers. */
    RoundingComparingDecimals: number;
    /**
     * How a rounding or comparing question is shown - plain, or a number
     * line. See ADR 0046.
     */
    RoundingDisplay: RoundingDisplayOptions;
    /** Whether a clock question is read off a face or drawn onto one. */
    TimeQuestion: TimeQuestionOptions;
    /** How precise the times on a clock sheet are. */
    TimeInterval: TimeIntervalOptions;
    /** How the two times in an elapsed-time problem are shown. Elapsed questions only. */
    TimeDisplay: TimeDisplayOptions;
    /**
     * Every elapsed problem crosses noon and every time prints a.m. or p.m.
     * Elapsed questions only; off draws exactly what it drew before (ADR 0090 §3).
     */
    CrossNoon?: boolean;
    /** How hard an order-of-operations expression is. Order of Operations format only. */
    OrderOfOperationsLevel: OrderOfOperationsLevelOptions;
    /** How an algebra question is asked (ADR 0028 §4). */
    AlgebraQuestion: AlgebraQuestionOptions;
    /** Largest value an algebra problem is built from (ADR 0028 §4). */
    AlgebraMaxValue: number;
    /**
     * Whether a format that would otherwise stay non-negative may use negative
     * numbers (ADR 0036 §2). Off by default: a fourth grader doing 4 + 3 x 2
     * and a sixth grader solving x + 7 = 12 have not met them. Applies to
     * Algebra and Order of Operations only.
     */
    AllowNegatives: boolean;
    /** What an integers sheet asks (ADR 0030 §3). Integers format only. */
    IntegerQuestion: IntegerQuestionOptions;
    /** Largest magnitude an integer problem is built from. */
    IntegerMaxValue: number;
    /**
     * Whether the three rational question types are decimals, fractions or both
     * (ADR 0122 §2). Integers format, rational types only. Basic tier.
     */
    RationalKind: RationalKindOptions;
    /** What a number-properties sheet asks (ADR 0032 §1). */
    NumberPropertyQuestion: NumberPropertyOptions;
    /** Largest number a property question is asked about. */
    NumberPropertyMaxValue: number;
    /** Which unit system a conversion sheet draws from (ADR 0032 §2). */
    ConversionQuestion: ConversionQuestionOptions;
    /** What kind of sequence a patterns sheet draws (ADR 0032 §5). */
    PatternQuestion: PatternQuestionOptions;
    /** How many terms a sequence shows, including the missing one. */
    PatternLength: number;
    /** Which way a Roman numeral question runs (ADR 0079 §2). */
    RomanQuestion: RomanQuestionOptions;
    /** The largest value a Roman numeral sheet asks about (ADR 0079 §4). */
    RomanMax: number;
    /** The largest number a magic square holds (ADR 0088 §4). */
    MagicSquareMax: number;
    /** What a line-plot sheet asks (ADR 0084 §5). */
    LinePlotQuestion: LinePlotQuestionOptions;
    /** The unit between a line plot's ticks: the format's difficulty dial (ADR 0084 §4). */
    LinePlotScale: LinePlotScaleOptions;
    /** The mark stacked above each value: X, or a dot for a dot plot (ADR 0084 §6). */
    LinePlotMark: LinePlotMarkOptions;
    /** What a histogram sheet asks (ADR 0099 §2). */
    HistogramQuestion: HistogramQuestionOptions;
    /** What a box-plot sheet asks (ADR 0099 §3). */
    BoxPlotQuestion: BoxPlotQuestionOptions;
    /** What a coordinate-plane sheet asks (ADR 0085 §2). */
    CoordinateQuestion: CoordinateQuestionOptions;
    /** First quadrant or four quadrants: the format's difficulty dial (ADR 0085 §4). */
    CoordinateGrid: CoordinateGridOptions;
    /** What a calendar sheet asks (ADR 0093 §4). */
    CalendarQuestion: CalendarQuestionOptions;
    /** How far a before-and-after question counts: the format's difficulty dial (ADR 0093 §5). */
    CalendarSpan: CalendarSpanOptions;
    /** Any month, or one named month (ADR 0093 §6). */
    CalendarMonth: CalendarMonthOptions;
    /** Which number system a number line asks about (ADR 0033 §2). */
    NumberLineQuestion: NumberLineQuestionOptions;
    /** The largest labelled value on a number line. */
    NumberLineRange: number;
    /** What a percent sheet asks (ADR 0034 §2). */
    PercentQuestion: PercentQuestionOptions;
    /** The largest base a percent question is asked about. */
    PercentMaxValue: number;
    /** What a ratio sheet asks (ADR 0035 §2). */
    RatioQuestion: RatioQuestionOptions;
    /** The largest value a ratio is built from. */
    RatioMaxValue: number;
    /** What a perimeter-and-area sheet asks (ADR 0038 §1). */
    ShapeQuestion: ShapeQuestionOptions;
    /** The largest side a rectangle may have. */
    ShapeMaxSide: number;
    /** What a volume sheet asks (ADR 0095 §3). */
    VolumeQuestion: VolumeQuestionOptions;
    /** The longest edge a labelled box may have (ADR 0095 §4). */
    VolumeMaxEdge: number;
    /** What a circles sheet asks (ADR 0106 §3). */
    CircleQuestion: CircleQuestionOptions;
    /** The largest radius a circle may have (ADR 0106 §1). */
    CircleMaxRadius: number;
    /** What a probability sheet asks (ADR 0108 §2). It has no dial. */
    ProbabilityQuestion: ProbabilityQuestionOptions;
    /** What a Pythagorean theorem sheet asks (ADR 0109 §2). */
    PythagoreanQuestion: PythagoreanQuestionOptions;
    /** The longest side, the hypotenuse, a Pythagorean problem may have (ADR 0109 §2). */
    PythagoreanMaxSide: number;
    /** What an averages sheet asks: mean, median, mode, range (ADR 0039 §2). */
    AverageQuestion: AverageQuestionOptions;
    /** How many values the list holds. Odd only, so a median is always a member. */
    AverageListLength: number;
    /** What a bar-graph sheet asks: reading, difference, total, largest (ADR 0039 §3). */
    BarGraphQuestion: BarGraphQuestionOptions;
    /** The top gridline a bar chart is scaled to. */
    BarGraphMaxValue: number;
    /**
     * How many questions share one drawn chart (ADR 0040 §3-4). A basic-tier
     * setting, not advanced: it changes what the sheet *is* - one chart per
     * question, or several questions about one chart - not how it looks.
     */
    BarGraphQuestionsPerChart: number;
    /** What a geometry sheet asks (ADR 0049 §1). Geometry format only. */
    GeometryQuestion: GeometryQuestionOptions;
    /**
     * Every angle a geometry sheet prints is a multiple of this (ADR 0049 §6).
     * The format's only graded dial, and the one Differentiation.ts steps -
     * the question type has no order. Does not apply to `ClassifyTriangle`,
     * which is about side lengths.
     */
    GeometryAngleStep: number;
    /** How a fact family is asked. Fact Families format only. */
    FactFamilyQuestion: FactFamilyOptions;
    /** What a fraction sheet asks: arithmetic (default), simplify, compare, … */
    FractionQuestion: FractionQuestionOptions;
    /**
     * Largest denominator a fraction-model drawing may use (ADR 0043 §3). Its
     * own field, separate from `MaxDenominator`: a model's bound is about
     * keeping the drawn cells countable, not about arithmetic difficulty, and
     * the two questions that read it (NameTheModel, CompareModels) never also
     * read MaxDenominator.
     */
    FractionModelMaxDenominator: number;
    /**
     * The printed language of the worksheet: word-problem stories (ADR 0016)
     * and, since ADR 0051 part 3, every other format's prompts and the shared
     * PDF chrome. Worksheet state, not the interface locale (ADR 0051 §7) -
     * it rides the `lang=` URL key and is part of what a seed reproduces.
     */
    Language: PrintedLanguage;
    /**
     * Print three ability tiers of the sheet in one PDF — one step easier, as
     * configured, one step harder — grouped by tier with the keys last. The
     * step per format is defined in Differentiation.ts. See ADR 0010.
     */
    DifferentiationSet: boolean;
    /**
     * Class-set mode: draw a different problem set for each student on the
     * roster rather than the same sheet with different names. The roster itself
     * is component state and is deliberately never stored or encoded — see
     * ADR 0011 §1 and the note in GenerateUrl.tsx / ProcessUrl.tsx.
     */
    ClassSetDistinctProblems: boolean;
}

export const DefaultSettings: ISettingsProps = {
    Format: ProblemFormatOptions.MathFacts,
    AllOperations: true,
    Page: Operations.Addition,
    Operators: [OperationOptions.filter(op => op.value === Operations.Addition)[0]],
    HalfPage: false,
    NumberOfProblemsPerPage: 30,
    NumberOfPages: 1,
    NumberRanges: [
        {
            Id: 1,
            NumberOfDigits: 1, 
            NumberRange: SingleDigitNumbers.Range,
            // The whole digit, not one number. This was [5], which opened the
            // home page and every page that inherits it on "n + 5", "n x 5",
            // "n ÷ 5", and reset the first number to 5 whenever "1 digit" was
            // clicked. A single table is a choice to make, not a default (ADR 0075).
            NumberRangeSelection: [...SingleDigitNumbers.Range]
        },
        {
            Id: 2,
            NumberOfDigits: 1, 
            NumberRange: SingleDigitNumbers.Range,
            NumberRangeSelection: SingleDigitNumbers.Range,
        },
    ],
    AnswerPages: false,
    WorkedAnswers: false,
    Presentation: { carryBoxes: false, alignmentGrid: false, largePrint: false },
    SpiralSections: [
        { format: ProblemFormatOptions.MathFacts, count: 8 },
        { format: ProblemFormatOptions.Fractions, count: 4 },
        { format: ProblemFormatOptions.PlaceValue, count: 3 },
    ],
    Remainders: false,
    Regrouping: RegroupingOptions.Both,
    MissingNumber: MissingNumberOptions.Off,
    Timed: false,
    StoryTypes: StoryTypeOptions.Simple,
    FactLevel: 0,
    FactLevelCeiling: 10,
    FactLevelSheet: 'test',
    FractionDenominators: FractionDenominatorOptions.Both,
    MaxDenominator: 12,
    MoneyMaxDollars: 10,
    MoneyQuestion: MoneyQuestionOptions.Arithmetic,
    MoneyCoins: [...DEFAULT_MONEY_COINS],
    DecimalPlaces: 2,
    DecimalMaxWhole: 20,
    PlaceValueQuestion: PlaceValueOptions.Mixed,
    PlaceValueDigits: 4,
    RoundingComparingQuestion: RoundingComparingOptions.Mixed,
    RoundingPlace: RoundingPlaceOptions.Mixed,
    RoundingComparingDigits: 4,
    RoundingComparingDecimals: 0,
    RoundingDisplay: RoundingDisplayOptions.Plain,
    TimeQuestion: TimeQuestionOptions.ReadTheClock,
    TimeInterval: TimeIntervalOptions.QuarterHour,
    TimeDisplay: TimeDisplayOptions.TwoClocks,
    CrossNoon: false,
    OrderOfOperationsLevel: OrderOfOperationsLevelOptions.TwoOperations,
    AlgebraQuestion: AlgebraQuestionOptions.Mixed,
    AlgebraMaxValue: 12,
    AllowNegatives: false,
    IntegerQuestion: IntegerQuestionOptions.Mixed,
    IntegerMaxValue: 12,
    RationalKind: RationalKindOptions.Both,
    NumberPropertyQuestion: NumberPropertyOptions.Mixed,
    NumberPropertyMaxValue: 50,
    ConversionQuestion: ConversionQuestionOptions.Mixed,
    PatternQuestion: PatternQuestionOptions.Mixed,
    PatternLength: 6,
    RomanQuestion: RomanQuestionOptions.Mixed,
    RomanMax: 100,
    MagicSquareMax: 20,
    LinePlotQuestion: LinePlotQuestionOptions.Mixed,
    // Halves, not whole numbers: the second step of the scale ladder, so a
    // differentiation set can step both ways from the default (ADR 0010 §4).
    LinePlotScale: LinePlotScaleOptions.Halves,
    LinePlotMark: LinePlotMarkOptions.X,
    HistogramQuestion: HistogramQuestionOptions.Mixed,
    BoxPlotQuestion: BoxPlotQuestionOptions.Mixed,
    CoordinateQuestion: CoordinateQuestionOptions.Mixed,
    CoordinateGrid: CoordinateGridOptions.FirstQuadrant,
    CalendarQuestion: CalendarQuestionOptions.Mixed,
    // The middle step, so a differentiation set can step both ways (ADR 0010 §4).
    CalendarSpan: CalendarSpanOptions.DaysAndWeeks,
    CalendarMonth: CalendarMonthOptions.Any,
    NumberLineQuestion: NumberLineQuestionOptions.Mixed,
    NumberLineRange: 20,
    PercentQuestion: PercentQuestionOptions.Mixed,
    PercentMaxValue: 100,
    RatioQuestion: RatioQuestionOptions.Mixed,
    RatioMaxValue: 50,
    ShapeQuestion: ShapeQuestionOptions.Mixed,
    ShapeMaxSide: 20,
    VolumeQuestion: VolumeQuestionOptions.Mixed,
    VolumeMaxEdge: 10,
    CircleQuestion: CircleQuestionOptions.Mixed,
    CircleMaxRadius: 10,
    ProbabilityQuestion: ProbabilityQuestionOptions.Mixed,
    PythagoreanQuestion: PythagoreanQuestionOptions.Mixed,
    PythagoreanMaxSide: 50,
    AverageQuestion: AverageQuestionOptions.Mixed,
    AverageListLength: 7,
    BarGraphQuestion: BarGraphQuestionOptions.Mixed,
    BarGraphMaxValue: 20,
    BarGraphQuestionsPerChart: 3,
    GeometryQuestion: GeometryQuestionOptions.Mixed,
    // The middle of GeometryAngleStepOptions, so a differentiation set can
    // step both ways from the default (ADR 0010 §4).
    GeometryAngleStep: 5,
    FactFamilyQuestion: FactFamilyOptions.FourEquations,
    FractionQuestion: FractionQuestionOptions.Arithmetic,
    // Second-smallest of FractionModelMaxDenominatorOptions, the pattern every
    // other MaxValue-style default follows (RatioMaxValue, AlgebraMaxValue, …).
    FractionModelMaxDenominator: 6,
    Language: 'en',
    DifferentiationSet: false,
    ClassSetDistinctProblems: false,
}

/**
 * Large print (ADR 0014 §3) is a different layout, so it is a different set of
 * counts (ADR 0050 §1, §2). `PdfGenerator/Page.tsx` drops math facts to four
 * across under large print where the grid takes five or ten, and the counts
 * never followed - so every math-facts count above 20 full / 12 half printed
 * on a second sheet, silently, for the accommodation whose whole point is that
 * the visitor cannot easily see that it did.
 *
 * Money and decimals keep their row count and take the size increase in height
 * alone, so they hold more: 25 full, and every half count they offer already
 * fits. Two pairs because the two layouts genuinely differ.
 *
 * Measured against the rendered PDF at every count, full page and half, keys
 * on and off, three seeds: math facts hold 20 full and 12 half, money and
 * decimals hold 25 full. Half 21 fit on one page and overprinted - seven to a
 * row - so money and decimals hold 15 half, as they do in normal print (ADR
 * 0089 As built).
 */
export const FullPageLargePrintOptions = [6, 10, 20];
export const HalfPageLargePrintOptions = [6, 10, 12];
export const FullPageLargePrintMoneyOptions = [12, 20, 25];
export const HalfPageLargePrintMoneyOptions = [10, 15];

export const HalfPageNumberOfProblemsOptions = [10, 15, 21, 30, 40];
export const HalfPageLongDivisionNumberOfProblemsOptions = [10, 15, 21];
export const HalfPageNumberOfWordProblemsOptions = [2, 4];
// 6 and 10 are the reduced-item accommodations (ADR 0014 §4) - the same layout
// code, just fewer problems. A named accommodation on many IEPs.
export const FullPageNumberOfProblemsOptions = [6, 10, 20, 25, 30, 60, 100];
export const FullPageLongDivisionNumberOfProblemsOptions = [20, 25, 30];
export const FullPageNumberOfWordProblemsOptions = [2, 4, 6, 8];
/**
 * Fractions are wider and taller than a fact, so far fewer fit on a sheet.
 *
 * Both arrays are measured against the real rendered PDF at every value, full
 * page and half, with answer pages on and off, at every question type that
 * uses the packed glyph grid. The half-page values only became true on
 * 2026-09-06: 9 and 12 had been spilling onto a second sheet since the format
 * shipped, because `problemsPerRow` in PdfGenerator/Page.tsx packed a half
 * page at the full page's density while `sizeFor` sized it at twice that.
 * The counts were right; the layout was not. See ADR 0047.
 */
export const FullPageNumberOfFractionProblemsOptions = [9, 12, 18, 24];
export const HalfPageNumberOfFractionProblemsOptions = [6, 9, 12];
/**
 * A fraction-model question draws one or two bars, taller than a fraction sum
 * (ADR 0043 §Consequences: "a model is taller than a fraction sum"). Its own
 * arrays rather than reusing FullPageNumberOfFractionProblemsOptions, so an
 * arithmetic or sub-skill fraction sheet is unaffected.
 *
 * One set serves both NameTheModel and CompareModels, sized to the tighter of
 * the two, which is NameTheModel - and only once answer pages are counted.
 * With answer pages off, NameTheModel holds up to 10 full / 4 half (the bar
 * plus a blank line); CompareModels holds up to 11 full / 4 half (a prompt
 * and one row of two bars with the symbol inline - no separate blank line, so
 * it packs a touch denser). But turning answer pages on very nearly halved
 * NameTheModel's room the first time this was measured: printing the answer
 * as a stacked `FractionGlyph` at the packed-grid font (`sizeFor`, up to 26pt
 * for a page this size) stacked *on top of* the bar, and the safe full-page
 * count with answer pages on fell to 5. The fix was in `Problems-UI/Fractions.tsx`
 * - the model questions now use place-value's plain two-tier font
 * (`problemCount > 8 ? font15 : font20`) and NameTheModel's answer prints as
 * one line of text (`numerator/denominator`) rather than a stacked glyph -
 * after which the answer-page count rose to 7 full / 3 half. CompareModels
 * was never affected: its answer is the same short symbol either way.
 *
 * These arrays are the smaller, answer-pages-on numbers, since Include Answer
 * Pages is an ordinary toggle a visitor can turn on for any count offered.
 * Measured against the rendered PDF, full page and half, with and without
 * answer pages, at every offered count.
 */
export const FullPageNumberOfFractionModelOptions = [4, 5, 6, 7];
export const HalfPageNumberOfFractionModelOptions = [2, 3];
// Money amounts are wider than a fact, so a row holds fewer of them.
export const FullPageNumberOfMoneyProblemsOptions = [12, 20, 25, 30];
// No 21: a half page of 21 sets seven to a row, and seven money or decimal
// columns overprint - each "+" lands on the end of the number before it. It
// passed the page count and failed the layout check (ADR 0089 As built). Six
// to a row would make four rows, and more rows is how a 21-problem decimals
// half sheet once spilled onto a second page (see `problemsPerRow`, Page.tsx).
export const HalfPageNumberOfMoneyProblemsOptions = [10, 15];
/**
 * A row of drawn coins is the widest thing this site prints, so far fewer fit
 * than a vertical money column. A different width class from arithmetic money -
 * not the arrays above. Measured against the rendered PDF. See ADR 0018 §6.
 */
export const FullPageNumberOfCoinProblemsOptions = [4, 6, 8];
export const HalfPageNumberOfCoinProblemsOptions = [2, 3, 4];
/** Making change is a line of text, not a coin row: closer to the money arrays. */
export const FullPageNumberOfChangeProblemsOptions = [8, 12, 16];
export const HalfPageNumberOfChangeProblemsOptions = [4, 6, 8];
// 7 + ___ = 12 is a line, not a stacked column - three per row, not five or
// ten - so the fact-grid counts of 60 and 100 do not apply. Measured.
export const FullPageNumberOfMissingNumberOptions = [12, 18, 24, 30];
export const HalfPageNumberOfMissingNumberOptions = [8, 12, 16];
/**
 * A place value question is a full-width sentence with a writing line under it,
 * so far fewer fit than a grid of facts. Measured against the rendered PDF: 16
 * is the most that fits on a sheet, and 20 spills onto a second page. Raising
 * these means re-measuring, not guessing - see PLACE_VALUE_MAX_PER_PAGE.
 */
export const PLACE_VALUE_MAX_PER_PAGE = 16;
export const PLACE_VALUE_MAX_PER_HALF_PAGE = 6;
/**
 * A clock face is a square that has to stay big enough to read, so a sheet
 * holds far fewer than a grid of facts. Measured against the rendered PDF.
 */
export const TIME_MAX_PER_PAGE = 12;
export const TIME_MAX_PER_HALF_PAGE = 6;
export const FullPageNumberOfTimeProblemsOptions = [6, 9, 12];
export const HalfPageNumberOfTimeProblemsOptions = [2, 4, 6];
/**
 * Elapsed time shows two times per problem. Two clock faces is the widest, so
 * far fewer fit than one face; two digital times are small. Measured against
 * the rendered PDF at TwoClocks, full page and half. See ADR 0017 §4.
 */
export const FullPageNumberOfElapsedTwoClockOptions = [4, 6, 8];
export const HalfPageNumberOfElapsedTwoClockOptions = [2, 3, 4];
export const FullPageNumberOfElapsedDigitalOptions = [10, 15, 20];
export const HalfPageNumberOfElapsedDigitalOptions = [4, 6, 8];
/**
 * Elapsed time on a number line is a full-width drawing - a prompt, the
 * line, and an answer rule - the same shape as the NumberLine format's own
 * problem, not the short two-clock or two-digital line (ADR 0044 §Consequences).
 * Its own arrays rather than a borrowed one, and measured against the
 * rendered PDF with answer pages on, which is the binding case (ADR 0043).
 */
export const FullPageNumberOfElapsedNumberLineOptions = [4, 5, 6, 7];
export const HalfPageNumberOfElapsedNumberLineOptions = [2, 3];
/**
 * A.M. or P.M. (ADR 0090 §1): one sentence and the two halves to circle, one
 * to a row. Measured against the rendered PDF with answer pages on.
 */
export const FullPageNumberOfAmPmOptions = [10, 12, 15];
export const HalfPageNumberOfAmPmOptions = [5, 6];

/**
 * An order-of-operations expression is a full-width line, one per row, like a
 * rounding prompt. Closer to PLACE_VALUE_MAX_PER_PAGE (16) than a fact grid.
 * Measured against the rendered PDF - not guessed downward. See ADR 0019 §5.
 */
export const FullPageNumberOfOrderOfOperationsOptions = [10, 14, 18, 22];
export const HalfPageNumberOfOrderOfOperationsOptions = [4, 6, 9];
// Algebra is a full-width expression row, the same width class as order of
// operations, so it inherits those counts rather than declaring its own. The
// plans README is explicit that a same-width-class format should not invent a
// narrower path "to be safe" - fewer per row means more rows, and more rows
// spill onto a second page.


/**
 * A fact family is a drawn triangle or number-bond diagram plus four equation
 * lines - a large unit. Far fewer per page than a fact grid. Measured against
 * the rendered PDF. See ADR 0020 §4.
 */
export const FullPageNumberOfFactFamilyOptions = [6, 9, 12];
/**
 * All three half-page counts are kept, but half 4 only became true with
 * ADR 0048: it had been drawing a 92pt diagram where two rows on a half page
 * hold 88pt, and spilled onto a second sheet while half *6* fitted, because
 * the larger count drew smaller. `Problems-UI/FactFamily.tsx` now takes the
 * small size past one row on a half page. Measured at every offered count,
 * full and half, keys on and off, across six seeds.
 */
export const HalfPageNumberOfFactFamilyOptions = [3, 4, 6];

/**
 * Geometry's sentences are longer than a place value prompt, so it does not
 * borrow that format's counts the way Percent, Ratio and Averages do - ADR
 * 0049 §1 assumed it would and the measurement said otherwise before it
 * shipped. "A triangle has sides of 12 cm, 12 cm and 7 cm. Is it scalene,
 * isosceles or equilateral?" is the longest sentence this site prints, and it
 * wraps to two lines at the font a ten-problem sheet uses.
 *
 * Measured against the real rendered PDF at every question type, full page and
 * half, with answer pages on and off, across four seeds: **nine fits a full
 * page and ten does not; four fits a half page and five does not.** Answer
 * pages are the binding case, as always - the printed answer is a line of text
 * where the blank is a rule, which costs about 9pt a problem.
 *
 * Raising either ceiling means re-measuring, not guessing. See ADR 0049's
 * "As built" note.
 */
export const FullPageNumberOfGeometryOptions = [4, 6, 8];
export const HalfPageNumberOfGeometryOptions = [2, 3, 4];

/**
 * Proportional Tables (ADR 0104 §5): a prompt, a two-row table and an
 * answer rule are taller than a sentence, so they do not borrow place value's
 * counts (ADR 0050). Measured against the rendered PDF in three languages with
 * keys on and off: eight fits a full page. Four a half page stays on one sheet
 * but prints text over text or on the footer (`npm run measure -- layout`,
 * ADR 0089), so a half page stops at three.
 */
export const FullPageNumberOfProportionalOptions = [4, 6, 8];
export const HalfPageNumberOfProportionalOptions = [2, 3];

/**
 * Proportional Graphs (ADR 0105 §5): a graph has to be big enough to read a
 * gridline, about as tall as four of the tables above, so it has counts of its
 * own too.
 */
export const FullPageNumberOfProportionalGraphOptions = [2, 3, 4];
export const HalfPageNumberOfProportionalGraphOptions = [1, 2];

export const FullPageNumberOfPlaceValueProblemsOptions = [8, 10, 12, 16];

/**
 * Percent's money questions (ADR 0120 §7): a sentence with an amount, a rate
 * and a question in it, a line longer than any of Percent's other prompts, so
 * they do not borrow place value's counts. Measured against the rendered PDF
 * in English, Spanish and French at the largest setting, keys on and off.
 */
export const FullPageNumberOfPercentMoneyOptions = [6, 8, 10];
export const HalfPageNumberOfPercentMoneyOptions = [3, 4];

/**
 * Writing Expressions on Algebra (ADR 0123 §6): one phrase to a row, and a long
 * one wraps to a second line, so these are not the plain Algebra counts. Measured
 * against the rendered PDF in English, Spanish and French at dial 50, keys on and
 * off; a count is here only if it passed.
 */
export const FullPageNumberOfWritingExpressionOptions = [10, 14, 18];
export const HalfPageNumberOfWritingExpressionOptions = [4, 6];

/**
 * Systems of Equations on Algebra (ADR 0139 §5): three lines to a problem, two
 * problems to a row, so fewer than the other Algebra types. Measured against the
 * rendered PDF in English, Spanish and French at dial 50 with negatives on, keys
 * on and off; a count is here only if it passed.
 */
export const FullPageNumberOfSystemsOptions = [10, 14];
export const HalfPageNumberOfSystemsOptions = [4, 6];

/**
 * The rational types on Integers (ADR 0122 §6): a line of decimals is longer than
 * an integer's, and a row of stacked fractions is a stack tall, so they do not
 * borrow order of operations' counts. A count has to hold for every
 * `RationalKind`, because the setting can change under a fixed count.
 */
export const FullPageNumberOfRationalOptions = [10, 14, 20];
export const HalfPageNumberOfRationalOptions = [4, 6, 8];

/**
 * Skip counting, and only skip counting, stops at ten.
 *
 * Every other count on this site is bounded by how much fits on the page.
 * This one is bounded by how many questions exist: skip counting is the
 * multiples of one number, and the step runs 2 to 12, so there are exactly
 * eleven distinct sequences at any length. Asking for twelve or sixteen does
 * not overflow anything - it prints the same sequence twice, and at sixteen it
 * prints five of them twice. `PatternGenerator.skipCounting` records the
 * eleven; this is the count array that respects it.
 *
 * Measured across four seeds: pinned skip counting yields 8 and 10 distinct at
 * those counts, and 11 at both 12 and 16. `Mixed` is unaffected - it draws
 * skip counting for only a third of its problems, and comes out fully distinct
 * even at sixteen - so this narrowing applies to the pinned question only.
 */
export const FullPageNumberOfSkipCountingOptions = [8, 10];
/**
 * Six was here and overflowed. A prompt-and-answer problem is a sentence plus
 * a full-width rule to write on, and twelve of them - six per half, twice on
 * the sheet - spill onto a third page. Measured against the rendered PDF while
 * building ADR 0032, which shares this array; the defect was Place Value's and
 * had been there since it shipped.
 */
export const HalfPageNumberOfPlaceValueProblemsOptions = [3, 4];

/**
 * A drawn number line is a prompt, a drawing and an answer rule - taller than
 * any prompt-and-answer sentence, so it does not borrow the place value counts
 * (ADR 0033 §5).
 *
 * Measured against the rendered PDF: eight spills onto a third page, seven
 * does not; four per half page spills, three does not.
 */
export const FullPageNumberOfNumberLineOptions = [4, 5, 6, 7];
export const HalfPageNumberOfNumberLineOptions = [2, 3];

/**
 * A labelled rectangle is roughly as tall as it is wide - the tallest thing
 * this site draws after a clock face - but two fit across a page, so the
 * counts are higher than the number line's.
 *
 * Measured, and the first guess was wrong: eight is four rows of two and fits;
 * nine puts a fifth row on the page and spills to a third sheet. Ten was in
 * this array until a page count said otherwise (ADR 0038 §5).
 *
 * Six was in the half-page array on the same terms and came out for the same
 * reason (ADR 0048). Three rows of a labelled rectangle, twice on one sheet,
 * do not fit: a shape row is about 161pt - a prompt that often wraps to two
 * lines, a drawing whose SVG box is the shape plus 14pt of label gap a side,
 * and an answer rule - so two halves of three rows want about 1116pt of A4's
 * 842. Unlike Fact Families' half 4, which shrank into place, this one has no
 * size that works: measured at every drawing height down to 30pt it still
 * spilled, and only a 10pt drawing fitted, which is no longer a drawing to
 * scale (ADR 0038 §2). Three per row was measured too and is no better - at a
 * third of the width the prompts wrap further, so two tall rows cost what
 * three short ones did. Same shape as
 * HalfPageNumberOfPlaceValueProblemsOptions above, which lost its six too.
 */
export const FullPageNumberOfShapeOptions = [2, 4, 6, 8];
/**
 * Half 4 came out with ADR 0050 §4: the Missing Side question type prints a
 * two-line prompt, and at four per half with an answer page it spilled. One
 * array sized to the tighter question type, on ADR 0043's precedent, rather
 * than a per-question pair for one value.
 */
export const HalfPageNumberOfShapeOptions = [2, 3];

/**
 * Scale Drawing (ADR 0107 As built): the same drawing plus a scale line under
 * it and prompts that wrap to three lines, so eight a full page and three a
 * half page ran into the footer, and the Spanish and French full 8 ran to a
 * second page. Counts of its own, without those two, on ADR 0050's precedent.
 */
export const FullPageNumberOfScaleDrawingOptions = [2, 4, 6];
export const HalfPageNumberOfScaleDrawingOptions = [2];

/**
 * Parallelogram, trapezoid, triangle and Polygon Area (ADR 0121 §6): a drawing
 * with a label above, a label below and a dashed height, so counts of their own
 * on ADR 0050's precedent. Started at full 4 and 6, half 2 and 3; `npm run
 * measure` holds what is left, in all three languages.
 */
export const FullPageNumberOfAreaFigureOptions = [4, 6];
export const HalfPageNumberOfAreaFigureOptions = [2, 3];

/**
 * Started from the shapes arrays above and cut by looking at rendered sheets
 * (ADR 0095 §As built): a box stands taller than a rectangle, and at eight a
 * page the last row's boxes went onto the next page. `npm run measure` holds
 * what is left, in all three languages. A cylinder, cone or sphere (ADR 0140
 * §6) takes the same counts: at 8 full its three-line prompt sends the last row
 * onto a second page, and 4 half runs into the footer (English, French, and
 * Spanish spheres), so it needs no counts of its own.
 */
export const FullPageNumberOfVolumeOptions = [2, 4, 6];
export const HalfPageNumberOfVolumeOptions = [2, 3];

/**
 * A circle is smaller than a box and sits two to a row like one, so it starts
 * with room for more (ADR 0106 §4). `npm run measure` holds what is left, in
 * all three languages.
 */
export const FullPageNumberOfCircleOptions = [4, 6, 8];
export const HalfPageNumberOfCircleOptions = [2, 3, 4];

/**
 * A spinner or a bag is about a circle's size, two to a row, and two spinners
 * share a cell, so it starts where circles do (ADR 0108 §3). `npm run measure`
 * holds what is left, in all three languages.
 */
export const FullPageNumberOfProbabilityOptions = [4, 6, 8];
export const HalfPageNumberOfProbabilityOptions = [2, 3, 4];

/**
 * A right triangle is about a circle's size, two to a row, and the converse
 * question is a sentence in the same cell, so it starts where circles do
 * (ADR 0109 §5). `npm run measure` holds what is left, in all three languages.
 */
export const FullPageNumberOfPythagoreanOptions = [4, 6, 8];
export const HalfPageNumberOfPythagoreanOptions = [2, 3, 4];

/**
 * These now count *questions*, not charts (ADR 0040 §4) - a group of
 * `BarGraphQuestionsPerChart` questions shares one drawn chart, amortised
 * above the group's rows rather than redrawn per question, so the chart can
 * afford to be bigger than 0039's 280 x 120. `PdfGenerator/Page.tsx` draws it
 * at 340 x 148 on a full page, and *shrinks* the half-page one from 0039's
 * 220 x 95 to 200 x 85 - see that file for the measurement that forced it.
 *
 * These counts are chosen independently of `BarGraphQuestionsPerChart`, so
 * they have to hold at *every* value that setting offers - and the worst case
 * is the lowest one (2), which draws the most charts for a given number of
 * questions. That is what rules out 8. Measured against the rendered PDF,
 * seeded, at every combination of the two settings:
 *
 * | questions per chart | 3 | 4 | 6 | 8 |
 * |---|---|---|---|---|
 * | 2 | 1 | 1 | 1 | **2** |
 * | 3 | 1 | 1 | 1 | **2** |
 * | 4 | 1 | 1 | 1 | 1 |
 *
 * 8 fits only at four questions per chart - two charts - and a visitor who
 * turned questions-per-chart down to 2 would silently get a second sheet. A
 * per-page count that is only safe at one value of another dial is not a safe
 * per-page count, so 8 is not offered. The alternative was to shrink the chart
 * until four of them fit, which would give back exactly what ADR 0040 §4 set
 * out to win. Six questions on one page is still half again as many as the
 * four charts 0039 managed, and every one of them is readable.
 *
 * The half page's tight case is 3 questions at 2 per chart - two charts on
 * half a sheet - and it is the case that set the half-page chart's size. At
 * 0039's 220 x 95 it spills; at 200 x 85 every half-page combination holds.
 * Trading a tenth of the chart's width for half again as many questions is
 * the right way round on a sheet this cramped.
 */
export const FullPageNumberOfBarGraphOptions = [3, 4, 6];
export const HalfPageNumberOfBarGraphOptions = [2, 3];
/**
 * `Total` and `Largest` have exactly one true answer each, so a chart supports
 * at most one of them (see the header on `BarGraphGenerator.ts`). A sheet
 * pinned to either therefore draws **one chart per question** - ADR 0040's
 * amortisation, which the arrays above were measured under, simply does not
 * hold - so a count here is a count of charts. ADR 0050 §3.
 *
 * The half page offers one value because two charts is what a half page holds.
 * Measured: three full and two half spill, at every seed.
 */
export const FullPageNumberOfSingleAnswerBarGraphOptions = [2, 3];
export const HalfPageNumberOfSingleAnswerBarGraphOptions = [2];
/**
 * Line plots count *questions*, up to four to a plot (ADR 0084 §5), so a page
 * of 8 is two plots. Measured against the rendered PDF in three languages,
 * and then looked at, because the half page is where the measurement alone
 * was wrong:
 *
 * - Full page: 8 fits at every question type and language; 12 (three plots)
 *   spills onto a second page.
 * - Half page: 4 (one plot) fits. 8 (two plots) passed the page count and
 *   was still unusable. A half sheet is a fixed 50% box, and rather than
 *   spill, its content was squeezed: answer rules ran through the questions,
 *   marks overlapped a plot's title, and the last line met the footer. Only
 *   a rendered image showed it (ADR 0084 As built).
 */
export const FullPageNumberOfLinePlotOptions = [4, 6, 8];
export const HalfPageNumberOfLinePlotOptions = [2, 4];
/**
 * A sheet pinned to a one-answer question (how many in all, most common,
 * range, median) draws a plot per question, as a pinned bar-graph `Total`
 * sheet draws a chart per question, so a count here is a count of plots.
 * Three plots fit a full page. Two on a half page pass the page count but run
 * the last answer rule into the footer, so a half page holds one.
 */
export const FullPageNumberOfSingleAnswerLinePlotOptions = [2, 3];
export const HalfPageNumberOfSingleAnswerLinePlotOptions = [1];
/**
 * A median, mode and range sheet asks three questions a plot, one of each, so
 * its counts are multiples of three: six questions is two plots, as eight is
 * on a Mixed sheet. Nine (three plots) spills a full page; a half page holds
 * one plot, as it does on a Mixed sheet. Measured, and looked at.
 */
export const FullPageNumberOfCenterAndSpreadLinePlotOptions = [3, 6];
/**
 * Coordinate-plane sheets count *questions*, four to a grid, and a grid sits
 * beside its questions (ADR 0085 §1), so a full page of 8 is two grids and a
 * half page of 4 is one. Measured against the rendered PDF in three
 * languages, at every question type on both grids, and looked at: beside the
 * grid, four questions are about as tall as the grid, so the half page is
 * not squeezed (ADR 0085 As built).
 */
export const FullPageNumberOfCoordinateOptions = [4, 8];
/**
 * Magic squares sit two across (ADR 0088 §4): a full page of eight is four rows,
 * a half page of two is one. Measured against the rendered PDF.
 */
/**
 * Calendar sheets draw one month a page and one question to a row under it
 * (ADR 0093 §2, §8). Measured against the rendered PDF in three languages at
 * every question type, and looked at:
 *
 * - Full page: 10 fits. 12 is one page, but on the answer key the last answer
 *   reaches into the footer band (the layout check, ADR 0089).
 * - Half page: 3 fits. 4 and 5 are one page and run into the footer the
 *   same way, on the worksheet as well as the key.
 */
export const FullPageNumberOfCalendarOptions = [4, 6, 8, 10];
export const HalfPageNumberOfCalendarOptions = [2, 3];
export const FullPageNumberOfMagicSquareOptions = [4, 6, 8];
export const HalfPageNumberOfMagicSquareOptions = [2];
export const HalfPageNumberOfCoordinateOptions = [2, 4];
/**
 * Slope from a Graph draws a grid a problem, two to a row (ADR 0126 §6), and
 * Slope from Two Points is text, two to a row. To be confirmed by npm run measure.
 */
export const FullPageNumberOfSlopeGraphOptions = [4, 6];
export const HalfPageNumberOfSlopeGraphOptions = [2];
export const FullPageNumberOfSlopePointsOptions = [8, 12, 16];
export const HalfPageNumberOfSlopePointsOptions = [4, 6, 8];
/**
 * A Triangles sheet asks three questions a triangle (ADR 0092 §4), so its
 * counts are multiples of three: two triangles a full page, one a half page,
 * the same grids a four-question sheet draws.
 */
export const FullPageNumberOfTriangleOptions = [3, 6];
export const HalfPageNumberOfTriangleOptions = [3];
/**
 * A Missing Corner sheet asks three questions a grid (ADR 0094 §3): the same
 * counts as Triangles. To be confirmed by npm run measure.
 */
export const FullPageNumberOfMissingCornerOptions = [3, 6];
export const HalfPageNumberOfMissingCornerOptions = [3];
export const HalfPageNumberOfCenterAndSpreadLinePlotOptions = [3];
/**
 * A histogram sheet asks three questions a histogram (ADR 0099 §4), so its
 * counts are whole drawings: two histograms a full page, one a half page.
 * To be confirmed by npm run measure.
 */
export const FullPageNumberOfHistogramOptions = [3, 6];
export const HalfPageNumberOfHistogramOptions = [3];
/**
 * A sheet pinned to How Many in All or Most has one question a histogram, so
 * it draws a histogram per question and a count is a count of histograms, as
 * a pinned bar-graph Total sheet's is (ADR 0050 §3): the bar graph's counts,
 * for a drawing of the same size. To be confirmed by npm run measure.
 */
export const FullPageNumberOfSingleAnswerHistogramOptions = [2, 3];
export const HalfPageNumberOfSingleAnswerHistogramOptions = [1];
/**
 * A box plot is shorter than a histogram, and three questions go with each
 * (ADR 0099 §4): one, two or three plots a full page, one a half page. To be
 * confirmed by npm run measure.
 */
export const FullPageNumberOfBoxPlotOptions = [3, 6, 9];
export const HalfPageNumberOfBoxPlotOptions = [3];
/**
 * A sheet pinned to Median, Range or Interquartile Range has one question a
 * plot, and one pinned to Quartiles two, so a count is a count of plots (ADR
 * 0050 §3): 2 to 4 a full page, 1 a half page for one question, and whole
 * plots of 2 for Quartiles. To be confirmed by npm run measure.
 */
export const FullPageNumberOfSingleAnswerBoxPlotOptions = [2, 3, 4];
export const HalfPageNumberOfSingleAnswerBoxPlotOptions = [1];
export const FullPageNumberOfQuartileBoxPlotOptions = [2, 4, 6];
export const HalfPageNumberOfQuartileBoxPlotOptions = [2];
/**
 * A rounding prompt is a full-width sentence, like place value; a comparison is
 * a short line and packs about three times as densely. One set of counts serves
 * both - sized for rounding, so a comparing-only page looks sparse. Measured.
 */
export const FullPageNumberOfRoundingComparingOptions = [8, 10, 12, 16];
/**
 * Six became five with ADR 0050 §4. A Rounding-only sheet is full-width
 * sentences all the way down, where the mixed sheet these counts were measured
 * on packs short comparisons between them; six of them with an answer page
 * spilled. Sized to the tighter question type (ADR 0043's precedent).
 */
export const HalfPageNumberOfRoundingComparingOptions = [3, 4, 5];
/**
 * A rounding or comparing question drawn on a number line is a full-width
 * unit - a prompt (or an inline pair) and the drawing beneath it - the same
 * width class as the NumberLine format's own problem and elapsed time's own
 * number line, not the packed sentence or the three-across comparison the
 * plain presentation uses. Its own arrays rather than a borrowed one
 * (ADR 0044 §Consequences made the same call).
 *
 * Measured by rendering a real PDF (not the jest-mocked stub) with answer
 * pages on, the binding case twice running (ADR 0043, ADR 0044). Rounding
 * pinned to the nearest ten is the tightest case - every problem draws a
 * line, and rounding's blank writing line underneath makes it a hair taller
 * than a comparing line, which has none. At that setting: 9 problems fit on
 * one full page and one key page (18 rows in this width class); 10 spills
 * the worksheet and the key each onto a second page. On a half page: 3 fit,
 * 4 spills. A Mixed-places sheet is never tighter than this, because every
 * problem that falls back to Plain (ADR 0046 §2) is shorter, not taller.
 */
export const FullPageNumberOfRoundingNumberLineOptions = [4, 6, 8, 9];
export const HalfPageNumberOfRoundingNumberLineOptions = [2, 3];
//export const RegroupingOptions = ['Regrouping Only', 'No Regrouping', 'Both'];